'use client'

import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from '@heroui/react'
import { RootState } from '@/src/store/store'
import { closeModal } from '@/src/store/slices/allModalSlice'
import { useCancelOrderMutation } from '@/src/store/rtkQueries/userPostAPI'
import { toast } from '@/src/lib/toast'
import Loader from '@/src/ui/Loader'
import ImageComponent from '@/src/components/common/ImageComponent'
import { OrderedProductsEntity } from '@/src/types/singleOrder'
import { formatAmount } from '@/src/utils/formatters'

export default function CancelOrderModal() {
    const dispatch = useDispatch()
    const { data } = useSelector((state: RootState) => state.allCommonModal)
    const [reason, setReason] = useState('')
    const [selectedItems, setSelectedItems] = useState<OrderedProductsEntity[]>([])
    const [cancelOrder, { isLoading }] = useCancelOrderMutation()

    const orderId = data?.orderId as string | undefined
    const orderItems = (data?.items as OrderedProductsEntity[] | undefined) ?? []
    const requestedItemIds = new Set((data?.requestedItemIds as string[] | undefined) ?? [])

    const allItemsSelected = orderItems.length > 0 && selectedItems.length === orderItems.length
    const someItemsSelected = selectedItems.length > 0 && !allItemsSelected

    const toggleItemSelection = (item: OrderedProductsEntity) => {
        setSelectedItems(prev =>
            prev.some(i => i.item_id === item.item_id)
                ? prev.filter(i => i.item_id !== item.item_id)
                : [...prev, item],
        )
    }

    const toggleSelectAll = () => {
        setSelectedItems(allItemsSelected ? [] : orderItems)
    }

    const handleCancelOrder = async () => {
        const trimmed = reason.trim()
        try {
            const response = await cancelOrder({
                orderId,
                body: {
                    reason: trimmed,
                    order_item_ids: selectedItems.map(item => item.item_id),
                },
            }).unwrap()
            if (response.http_status_code === 200 || response.http_status_code === 201) {
                toast.success(response.message || 'Order cancelled successfully')
                dispatch(closeModal())
            }
        } catch (error) {
            console.error(error)
        }
    }

    const canSubmit = Boolean(orderId && reason.trim() && selectedItems.length > 0)

    return (
        <div className="flex flex-col gap-5 py-1">
            {isLoading && <Loader />}

            <div className="space-y-1.5 text-center sm:text-left">
                <h3 className="text-base font-semibold text-body-color">Cancel order</h3>
                <p className="text-sm text-body-color/60">
                    Select the products you want to cancel. This cannot be undone.
                </p>
            </div>

            <div className="input_container">
                <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="form_label mb-0">
                        Products <span className="required_field">*</span>
                    </span>
                    {orderItems.length > 1 && (
                        <button
                            type="button"
                            onClick={toggleSelectAll}
                            disabled={isLoading}
                            className="text-xs font-medium text-body-color/70 hover:text-body-color disabled:opacity-50"
                        >
                            {allItemsSelected ? 'Deselect all' : 'Select all'}
                        </button>
                    )}
                </div>
                <div className="space-y-2 max-h-[220px] overflow-y-auto pr-0.5">
                    {orderItems.length === 0 ? (
                        <p className="text-sm text-body-color/50">No products available for this order.</p>
                    ) : (
                        orderItems.map(item => {
                            const isSelected = selectedItems.some(i => i.item_id === item.item_id)
                            const isRequested = requestedItemIds.has(item.item_id)
                            return (
                                <button
                                    key={item.item_id}
                                    type="button"
                                    onClick={() => !isRequested && toggleItemSelection(item)}
                                    disabled={isLoading || isRequested}
                                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl border transition-colors text-left ${isRequested ? 'cursor-not-allowed opacity-60' : 'cursor-pointer disabled:opacity-60'} ${isSelected
                                        ? 'border-body-color bg-body-color/4'
                                        : 'border-muted-color hover:border-body-color/30'
                                        }`}
                                >
                                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-background border border-muted-color shrink-0">
                                        <ImageComponent src={item.image} alt={item.title} object_cover={true} priority={false} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-body-color truncate">{item.title}</p>
                                        <p className="text-xs text-body-color/50 mt-0.5">
                                            Size: {item.size} · Qty: {item.quantity}
                                        </p>
                                        <p className="text-xs font-medium text-body-color/60 mt-0.5">
                                            {formatAmount(item.line_total, item.currency ?? 'EUR')}
                                        </p>
                                        {isRequested && (
                                            <p className="text-xs text-red-500 mt-1">Cancellation already requested</p>
                                        )}
                                    </div>
                                    <div
                                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected
                                            ? 'border-body-color bg-body-color'
                                            : 'border-muted-color'
                                            }`}
                                        aria-hidden
                                    >
                                        {isSelected && (
                                            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                                                <path d="M2 5.5l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                </button>
                            )
                        })
                    )}
                </div>
                {someItemsSelected && (
                    <p className="text-xs text-body-color/50 mt-2">
                        {selectedItems.length} of {orderItems.length} selected
                    </p>
                )}
            </div>

            <div className="input_container">
                <label htmlFor="cancel-order-reason" className="form_label">
                    Reason <span className="required_field">*</span>
                </label>
                <textarea
                    id="cancel-order-reason"
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Changed my mind"
                    className="form_input min-h-[88px] resize-y"
                    disabled={isLoading}
                />
            </div>

            <div className="flex gap-3">
                <Button
                    onPress={() => dispatch(closeModal())}
                    isDisabled={isLoading}
                    className="flex-1 global_btn outline_secondary rounded_full"
                >
                    Keep order
                </Button>
                <Button
                    onPress={handleCancelOrder}
                    isDisabled={isLoading || !canSubmit}
                    className="flex-1 global_btn rounded_full bg-red-500 text-white"
                >
                    {isLoading ? 'Cancelling...' : 'Cancel order'}
                </Button>
            </div>
        </div>
    )
}
